/**
 * ThemedButton.js — shared Primary/Ghost/Danger button components driven by
 * ThemeContext's T.btnPrimary/T.btnGhost/T.btnDanger tokens (see
 * utils/themeStyles.js), which in turn follow the admin's chosen
 * Button & Status Style (Config Master → Branding). Use these instead of a
 * local ad-hoc button style object so every screen's buttons stay visually
 * consistent and rebrand together when the style/colors change.
 *
 * Purely a rendering swap — same props as a plain <button>, so replacing an
 * existing button with one of these should not change any behavior.
 *
 * Real hover/press feedback is handled here (not CSS :hover, which inline
 * styles can't express): a filled button (solid/soft styles) darkens
 * slightly and lifts its shadow on hover, then flattens on press; an
 * outline-style button (transparent at rest) fills in with a light tint on
 * hover instead, since there's no fill to darken. Without this, a button
 * gives no visual response to the mouse at all — this was the concrete
 * cause of buttons "not feeling like buttons."
 */
import React, { useState } from "react";
import { useTheme, getT } from "../ThemeContext";

function base(size) {
  return {
    cursor: "pointer",
    borderRadius: 9,
    padding: size === "sm" ? "6px 14px" : "9px 20px",
    fontSize: size === "sm" ? 12 : 13,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    fontFamily: "inherit",
    whiteSpace: "nowrap",
    transition: "filter .12s ease, box-shadow .15s ease, transform .08s ease, background .15s ease",
  };
}

function disabledStyle(disabled) {
  return disabled ? { opacity: 0.45, cursor: "not-allowed" } : {};
}

function useInteraction(disabled) {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  const handlers = disabled ? {} : {
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => { setHover(false); setPressed(false); },
    onMouseDown:  () => setPressed(true),
    onMouseUp:    () => setPressed(false),
  };
  return { hover, pressed, handlers };
}

// Merges a button's theme tokens with its current hover/press state into a
// final inline style. `tokens.hoverBackground` (outline style only) swaps
// the background on hover instead of using filter, since a transparent
// button has no fill for a brightness filter to act on.
function interactiveStyle(tokens, hover, pressed, disabled) {
  if (disabled || !tokens) return { boxShadow: tokens?.shadow, ...tokens };
  const shadow = pressed ? tokens.shadowPress : hover ? tokens.shadowHover : tokens.shadow;
  const extra = { boxShadow: shadow, transform: pressed ? "translateY(1px)" : "none" };
  if (tokens.hoverBackground) {
    if (hover || pressed) extra.background = tokens.hoverBackground;
  } else if (hover || pressed) {
    extra.filter = pressed ? "brightness(0.88)" : "brightness(0.94)";
  }
  return { ...tokens, ...extra };
}

export function PrimaryButton({ children, style, disabled, size, ...props }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const { hover, pressed, handlers } = useInteraction(disabled);
  return (
    <button {...props} {...handlers} disabled={disabled}
      style={{ ...base(size), ...interactiveStyle(T.btnPrimary, hover, pressed, disabled), ...disabledStyle(disabled), ...style }}>
      {children}
    </button>
  );
}

export function GhostButton({ children, style, disabled, size, ...props }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const { hover, pressed, handlers } = useInteraction(disabled);
  return (
    <button {...props} {...handlers} disabled={disabled}
      style={{ ...base(size), ...interactiveStyle(T.btnGhost, hover, pressed, disabled), ...disabledStyle(disabled), ...style }}>
      {children}
    </button>
  );
}

export function DangerButton({ children, style, disabled, size, ...props }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const { hover, pressed, handlers } = useInteraction(disabled);
  return (
    <button {...props} {...handlers} disabled={disabled}
      style={{ ...base(size), ...interactiveStyle(T.btnDanger, hover, pressed, disabled), ...disabledStyle(disabled), ...style }}>
      {children}
    </button>
  );
}
